import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { useToast } from "@/hooks/use-toast";
import pdf2md from "@opendocsg/pdf2md";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Upload, X, Loader2, ChevronDown, CheckCircle, AlertCircle, Minus } from "lucide-react";
import type { LateralJobPosition, LateralCVEvaluationResult } from "@/types/database";

interface CVEntry {
  id: string;
  file: File;
  jobPositionId: string;
  salaryExpectation: string;
  yearsOfExperience: string;
  aiDetectExperience: boolean;
  status: "idle" | "parsing" | "evaluating" | "done" | "error";
  error?: string;
  result?: LateralCVEvaluationResult & { resumeMarkdown?: string };
}

let _id = 0;
const uid = () => String(++_id);

export default function BulkCVEvaluationPage() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<CVEntry[]>([]);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const { data: positions } = useQuery({
    queryKey: ["lateral-job-positions-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lateral_job_positions")
        .select("id, job_title, job_description, ai_evaluation_prompt")
        .eq("is_active", true)
        .order("job_title");
      if (error) throw error;
      return data as (Pick<LateralJobPosition, "id" | "job_title" | "job_description" | "ai_evaluation_prompt">)[];
    },
  });

  const setEntry = (id: string, patch: Partial<CVEntry>) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const newEntries: CVEntry[] = Array.from(files)
      .filter((f) => f.type === "application/pdf")
      .map((file) => ({
        id: uid(),
        file,
        jobPositionId: "",
        salaryExpectation: "",
        yearsOfExperience: "",
        aiDetectExperience: false,
        status: "idle",
      }));
    if (!newEntries.length) {
      toast({ title: "Only PDF files are supported", variant: "destructive" });
      return;
    }
    setEntries((prev) => [...prev, ...newEntries]);
  };

  const evaluateOne = async (entry: CVEntry): Promise<void> => {
    const position = positions?.find((p) => p.id === entry.jobPositionId);

    // Step 1: PDF → Markdown in the browser (no upload round-trip needed)
    setEntry(entry.id, { status: "parsing" });
    let resumeMarkdown = "";
    try {
      const arrayBuffer = await entry.file.arrayBuffer();
      resumeMarkdown = await pdf2md(new Uint8Array(arrayBuffer));
    } catch (err: any) {
      setEntry(entry.id, { status: "error", error: `Failed to read CV: ${err.message}` });
      return;
    }

    // Step 2: Evaluate
    setEntry(entry.id, { status: "evaluating" });
    try {
      const result = await invokeEdgeFunction<LateralCVEvaluationResult>("evaluate-lateral-cv", {
        body: {
          resumeMarkdown,
          jobTitle: position?.job_title ?? "",
          jobDescription: position?.job_description ?? "",
          aiEvaluationPrompt: position?.ai_evaluation_prompt ?? "",
          expectedCTC: entry.salaryExpectation,
          yearsOfExperience: entry.aiDetectExperience ? "AI to detect" : entry.yearsOfExperience,
        },
      });
      setEntry(entry.id, { status: "done", result: { ...result, resumeMarkdown } });
    } catch (err: any) {
      setEntry(entry.id, { status: "error", error: `Evaluation failed: ${err.message}` });
    }
  };

  const handleEvaluateAll = async () => {
    const toEvaluate = entries.filter((e) => e.status === "idle" || e.status === "error");
    if (!toEvaluate.length) return;

    const missing = toEvaluate.filter((e) => !e.jobPositionId);
    if (missing.length) {
      toast({ title: "Please select a job position for all CVs", variant: "destructive" });
      return;
    }

    setIsEvaluating(true);
    await Promise.all(toEvaluate.map(evaluateOne));
    setIsEvaluating(false);
    toast({ title: "Evaluation complete" });
  };

  const statusIcon = (entry: CVEntry) => {
    if (entry.status === "done") return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (entry.status === "error") return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (["parsing", "evaluating"].includes(entry.status))
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const statusLabel = (entry: CVEntry) => {
    const map: Record<CVEntry["status"], string> = {
      idle: "Ready",
      parsing: "Reading CV...",
      evaluating: "Evaluating...",
      done: "Done",
      error: "Error",
    };
    return map[entry.status];
  };

  const scoreColor = (score: number) =>
    score >= 70 ? "text-green-600" : score >= 50 ? "text-yellow-600" : "text-red-600";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bulk CV Evaluation</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload multiple CVs and screen them in parallel using AI
        </p>
      </div>

      {/* Drop zone */}
      <div
        className="border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition-colors bg-gray-50"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          addFiles(e.dataTransfer.files);
        }}
      >
        <Upload className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">Drop PDF files here or click to browse</p>
        <p className="text-xs text-gray-400 mt-1">Multiple files supported</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {entries.length > 0 && (
        <>
          <div className="space-y-3">
            {entries.map((entry) => (
              <Card key={entry.id} className="overflow-hidden">
                <CardContent className="p-4">
                  {/* Header row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {statusIcon(entry)}
                      <span className="text-sm font-medium truncate" title={entry.file.name}>
                        {entry.file.name}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{statusLabel(entry)}</span>
                    </div>
                    {entry.status === "idle" || entry.status === "error" ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        onClick={() => setEntries((p) => p.filter((e) => e.id !== entry.id))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>

                  {/* Config row (only show when idle/error) */}
                  {(entry.status === "idle" || entry.status === "error") && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Job Position *</Label>
                        <Select
                          value={entry.jobPositionId}
                          onValueChange={(v) => setEntry(entry.id, { jobPositionId: v })}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Select position" />
                          </SelectTrigger>
                          <SelectContent>
                            {positions?.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.job_title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Salary Expectation</Label>
                        <Input
                          className="h-8 text-xs"
                          placeholder="e.g. 18 LPA"
                          value={entry.salaryExpectation}
                          onChange={(e) => setEntry(entry.id, { salaryExpectation: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Years of Experience</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            className="h-8 text-xs"
                            placeholder="e.g. 5"
                            value={entry.yearsOfExperience}
                            disabled={entry.aiDetectExperience}
                            onChange={(e) => setEntry(entry.id, { yearsOfExperience: e.target.value })}
                          />
                          <label className="flex items-center gap-1 cursor-pointer whitespace-nowrap text-xs text-gray-500">
                            <Checkbox
                              checked={entry.aiDetectExperience}
                              onCheckedChange={(v) => setEntry(entry.id, { aiDetectExperience: !!v })}
                            />
                            AI detect
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {entry.status === "error" && entry.error && (
                    <p className="mt-2 text-xs text-red-500">{entry.error}</p>
                  )}

                  {/* Result */}
                  {entry.status === "done" && entry.result && (
                    <Collapsible className="mt-3">
                      <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900">
                        <span className={`text-xl font-bold ${scoreColor(entry.result.score)}`}>
                          {entry.result.score}/100
                        </span>
                        <span className="text-gray-400">—</span>
                        <span>View Details</span>
                        <ChevronDown className="h-4 w-4" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">Pros</p>
                            <ul className="space-y-1">
                              {entry.result.pros.map((pro, i) => (
                                <li key={i} className="text-sm text-gray-700 flex gap-1.5">
                                  <span className="text-green-500 mt-0.5">+</span>
                                  {pro}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Cons</p>
                            <ul className="space-y-1">
                              {entry.result.cons.map((con, i) => (
                                <li key={i} className="text-sm text-gray-700 flex gap-1.5">
                                  <span className="text-red-500 mt-0.5">−</span>
                                  {con}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        {entry.result.considerations && (
                          <div>
                            <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">
                              If proceeding, keep in mind
                            </p>
                            <div className="text-sm text-gray-700 prose prose-sm max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {entry.result.considerations}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-gray-500">
              {entries.filter((e) => e.status === "done").length}/{entries.length} evaluated
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEntries([])}
                disabled={isEvaluating}
              >
                Clear All
              </Button>
              <Button
                onClick={handleEvaluateAll}
                disabled={isEvaluating || entries.every((e) => e.status === "done")}
              >
                {isEvaluating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Evaluating...
                  </>
                ) : (
                  "Evaluate All"
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
